/**
 * LogisticsService — CRUD for logistics vehicles, drivers, and assignments.
 * Source of truth is the GuestGuru API; in-memory cache is the fast read layer.
 */

import { z, VehicleSchema, DriverSchema, GuestAssignmentSchema } from '@guestguru/shared';
import { Vehicle, Driver, GuestAssignment } from '../types/ui/Logistics';
import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';

// ── API response shapes (snake_case from @guestguru/shared) ──
type ApiVehicle = z.infer<typeof VehicleSchema>;
type ApiDriver = z.infer<typeof DriverSchema>;
type ApiAssignment = z.infer<typeof GuestAssignmentSchema>;

// ── In-memory caches ──────────────────────────────────────────────────────────

let vehicleCache: Vehicle[] = [];
let driverCache: Driver[] = [];
let assignmentCache: GuestAssignment[] = [];

/**
 * These create/update/delete calls are fire-and-forget (the cache updates
 * synchronously and optimistically, before the network call resolves), so a
 * rejected request has nowhere to throw to. It used to be swallowed
 * entirely — a validation error meant the UI showed success (button flipped,
 * cache updated) while nothing actually persisted, and only a page reload
 * revealed it hadn't. This at least surfaces it as a toast via
 * LogisticsManager's listener, in addition to the console log.
 */
function reportSaveError(entity: string, err: unknown): void {
  console.error(`${entity} save failed:`, err);
  const message = err instanceof Error ? err.message : `Could not save the ${entity.toLowerCase()} — please try again.`;
  window.dispatchEvent(new CustomEvent('logistics-save-error', { detail: message }));
}

/**
 * The shared schema does not list `archived`, so it is read defensively off the
 * raw row (as `archived` or `archived_at`). Dropping it made every archived
 * vehicle, driver and movement look active after a reload, so the archived
 * view, Restore and Delete forever never worked.
 */
export function isArchivedRow(raw: unknown): boolean {
  const row = raw as Record<string, unknown> | null | undefined;
  return row?.archived === true || !!row?.archived_at;
}

export type LogisticsLoadKind = 'vehicles' | 'drivers' | 'movements';
export const LOGISTICS_LOAD_STATE_EVENT = 'logistics-load-state';

// A failed load (the 30 s background refresh included) never raises a toast:
// it is logged, the stale cache stays, and the failure is kept here so the
// Logistics screen can show an error box with Retry while someone is on it.
let loadErrors: Partial<Record<LogisticsLoadKind, unknown>> = {};

export function getLogisticsLoadErrors(): Partial<Record<LogisticsLoadKind, unknown>> {
  return loadErrors;
}

function setLoadError(what: LogisticsLoadKind, err: unknown | null): void {
  const had = what in loadErrors;
  if (err === null && !had) return;
  const next = { ...loadErrors };
  if (err === null) delete next[what]; else next[what] = err;
  loadErrors = next;
  window.dispatchEvent(new CustomEvent(LOGISTICS_LOAD_STATE_EVENT, { detail: loadErrors }));
}

function reportLoadError(what: LogisticsLoadKind, err: unknown): void {
  console.warn(`${what} load failed:`, err);
  setLoadError(what, err);
}

// ── Vehicles ──────────────────────────────────────────────────────────────────

export function loadVehicles(): Vehicle[] {
  return vehicleCache;
}

export function createVehicle(data: Omit<Vehicle, 'id'>): Vehicle {
  const vehicle: Vehicle = { ...data, id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` };
  vehicleCache = [...vehicleCache, vehicle];
  apiPost(planningPath('/logistics/vehicles'), vehicle).catch(err => reportSaveError('Vehicle', err));
  return vehicle;
}

export function updateVehicle(id: string, data: Partial<Omit<Vehicle, 'id'>>): Vehicle | null {
  const idx = vehicleCache.findIndex(v => v.id === id);
  if (idx === -1) return null;
  const updated: Vehicle = { ...vehicleCache[idx], ...data };
  vehicleCache = vehicleCache.map((v, i) => i === idx ? updated : v);
  apiPut(planningPath(`/logistics/vehicles/${id}`), updated).catch(err => reportSaveError('Vehicle', err));
  return updated;
}

/** Soft-delete: mark vehicle as archived */
export function archiveVehicle(id: string): Vehicle | null {
  return updateVehicle(id, { archived: true });
}

/** Restore an archived vehicle */
export function restoreVehicle(id: string): Vehicle | null {
  return updateVehicle(id, { archived: false });
}

/** Permanent delete — only allowed for already-archived vehicles */
/**
 * Delete forever: only an archived vehicle, and the local copy is removed only
 * once the server has confirmed. A refusal throws so the dialog can say why.
 */
export async function deleteVehicle(id: string): Promise<void> {
  const target = vehicleCache.find(v => v.id === id);
  if (!target || !target.archived) throw new Error('Archive this vehicle first. Only archived records can be deleted forever.');
  await apiDelete(planningPath(`/logistics/vehicles/${id}`));
  vehicleCache = vehicleCache.filter(v => v.id !== id);
}

export async function refreshVehiclesFromApi(): Promise<Vehicle[]> {
  try {
    const raw = await apiGet<{ vehicles: ApiVehicle[] }>(planningPath('/logistics/vehicles'));
    const items = raw.vehicles ?? [];
    const normalised: Vehicle[] = items.map(v => ({
      id: String(v.id),
      name: v.name,
      type: (v.type ?? 'other') as Vehicle['type'],
      license_plate: v.license_plate ?? '',
      color: v.color ?? undefined,
      capacity: v.capacity,
      driver_id: v.driver_id != null ? String(v.driver_id) : undefined,
      features: (v.features ?? []) as string[],
      status: (v.status ?? 'available') as Vehicle['status'],
      notes: v.notes ?? undefined,
      archived: isArchivedRow(v),
      created_at: v.created_at,
    }));
    vehicleCache = normalised;
    setLoadError('vehicles', null);
    return vehicleCache;
  } catch (err) {
    reportLoadError('vehicles', err);
    return vehicleCache;
  }
}

// ── Drivers ───────────────────────────────────────────────────────────────────

export function loadDrivers(): Driver[] {
  return driverCache;
}

export function createDriver(data: Omit<Driver, 'id'>): Driver {
  const driver: Driver = { ...data, id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` };
  driverCache = [...driverCache, driver];
  apiPost(planningPath('/logistics/drivers'), driver).catch(err => reportSaveError('Driver', err));
  return driver;
}

/**
 * Creates a driver and waits for the API's answer.
 *
 * `createDriver` saves optimistically and reports a failure later through the
 * `logistics-save-error` event, which a form page that has already navigated
 * away cannot show next to its fields. This variant adds the driver to the
 * cache, awaits the POST, and on failure takes the driver back out and rethrows
 * the ApiError (with its field errors) so the form can stay open and show them.
 */
export async function createDriverStrict(data: Omit<Driver, 'id'>): Promise<Driver> {
  const driver: Driver = { ...data, id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` };
  const before = driverCache;
  driverCache = [...driverCache, driver];
  try {
    await apiPost(planningPath('/logistics/drivers'), driver);
  } catch (err) {
    driverCache = before;
    throw err;
  }
  return driver;
}

export function updateDriver(id: string, data: Partial<Omit<Driver, 'id'>>): Driver | null {
  const idx = driverCache.findIndex(d => d.id === id);
  if (idx === -1) return null;
  const updated: Driver = { ...driverCache[idx], ...data };
  driverCache = driverCache.map((d, i) => i === idx ? updated : d);
  apiPut(planningPath(`/logistics/drivers/${id}`), updated).catch(err => reportSaveError('Driver', err));
  return updated;
}

/** Soft-delete: mark driver as archived */
export function archiveDriver(id: string): Driver | null {
  return updateDriver(id, { archived: true });
}

/** Restore an archived driver */
export function restoreDriver(id: string): Driver | null {
  return updateDriver(id, { archived: false });
}

/** Permanent delete — only allowed for already-archived drivers */
/**
 * Delete forever: only an archived driver, and the local copy is removed only
 * once the server has confirmed. A refusal throws so the dialog can say why.
 */
export async function deleteDriver(id: string): Promise<void> {
  const target = driverCache.find(d => d.id === id);
  if (!target || !target.archived) throw new Error('Archive this driver first. Only archived records can be deleted forever.');
  await apiDelete(planningPath(`/logistics/drivers/${id}`));
  driverCache = driverCache.filter(d => d.id !== id);
}

export async function refreshDriversFromApi(): Promise<Driver[]> {
  try {
    const raw = await apiGet<{ drivers: ApiDriver[] }>(planningPath('/logistics/drivers'));
    const items = raw.drivers ?? [];
    const normalised: Driver[] = items.map(d => ({
      id: String(d.id),
      name: d.name,
      phone: d.phone ?? '',
      email: d.email ?? undefined,
      vehicle_id: d.vehicle_id != null ? String(d.vehicle_id) : undefined,
      notes: d.notes ?? undefined,
      emergency_contact: d.emergency_contact ?? undefined,
      emergency_phone: d.emergency_phone ?? undefined,
      archived: isArchivedRow(d),
      created_at: d.created_at,
    }));
    driverCache = normalised;
    setLoadError('drivers', null);
    return driverCache;
  } catch (err) {
    reportLoadError('drivers', err);
    return driverCache;
  }
}

// ── Assignments ───────────────────────────────────────────────────────────────

export function loadAssignments(): GuestAssignment[] {
  return assignmentCache;
}

export function createAssignment(data: Omit<GuestAssignment, 'id'>): GuestAssignment {
  const assignment: GuestAssignment = { ...data, id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` };
  assignmentCache = [...assignmentCache, assignment];
  apiPost(planningPath('/logistics/assignments'), assignment).catch(err => reportSaveError('Movement', err));
  return assignment;
}

export function updateAssignment(id: string, data: Partial<Omit<GuestAssignment, 'id'>>): GuestAssignment | null {
  const idx = assignmentCache.findIndex(a => a.id === id);
  if (idx === -1) return null;
  const updated: GuestAssignment = { ...assignmentCache[idx], ...data };
  assignmentCache = assignmentCache.map((a, i) => i === idx ? updated : a);
  apiPut(planningPath(`/logistics/assignments/${id}`), updated).catch(err => reportSaveError('Movement', err));
  return updated;
}

/** Soft-delete: mark assignment as archived */
export function archiveAssignment(id: string): GuestAssignment | null {
  return updateAssignment(id, { archived: true });
}

/** Restore an archived assignment */
export function restoreAssignment(id: string): GuestAssignment | null {
  return updateAssignment(id, { archived: false });
}

/** Permanent delete — only allowed for already-archived assignments */
/**
 * Delete forever: only an archived movement, and the local copy is removed only
 * once the server has confirmed. A refusal throws so the dialog can say why.
 */
export async function deleteAssignment(id: string): Promise<void> {
  const target = assignmentCache.find(a => a.id === id);
  if (!target || !target.archived) throw new Error('Archive this movement first. Only archived records can be deleted forever.');
  await apiDelete(planningPath(`/logistics/assignments/${id}`));
  assignmentCache = assignmentCache.filter(a => a.id !== id);
}

export async function refreshAssignmentsFromApi(): Promise<GuestAssignment[]> {
  try {
    const raw = await apiGet<{ assignments: ApiAssignment[] }>(planningPath('/logistics/assignments'));
    const items = raw.assignments ?? [];
    const normalised: GuestAssignment[] = items.map(a => ({
      id: String(a.id),
      guest_name: a.guest_name,
      guest_phone: a.guest_phone ?? undefined,
      vehicle_id: a.vehicle_id != null ? String(a.vehicle_id) : '',
      pickup_location: a.pickup_location ?? '',
      dropoff_location: a.dropoff_location ?? '',
      pickup_time: a.pickup_time ?? '',
      dropoff_time: a.dropoff_time ?? undefined,
      notes: a.notes ?? undefined,
      status: (a.status ?? 'pending') as GuestAssignment['status'],
      plus_ones: a.plus_ones,
      // `movement_type` isn't in the @guestguru/shared schema yet; read it
      // defensively off the raw payload (as `type` or `movement_type`) so it
      // round-trips once the backend adds support, and default to 'pickup'.
      movement_type: ((a as unknown as Record<string, unknown>).movement_type
        ?? (a as unknown as Record<string, unknown>).type
        ?? 'pickup') as GuestAssignment['movement_type'],
      archived: isArchivedRow(a),
      created_at: a.created_at,
      // Same passthrough reasoning as movement_type — guests isn't in the
      // shared schema (see the guest_ids UI-only note on GuestAssignment).
      guests: (((a as unknown as Record<string, unknown>).guests ?? []) as { id: number; name: string }[])
        .map(g => ({ id: String(g.id), name: g.name })),
      guest_ids: (((a as unknown as Record<string, unknown>).guests ?? []) as { id: number; name: string }[])
        .map(g => String(g.id)),
    }));
    assignmentCache = normalised;
    setLoadError('movements', null);
    return assignmentCache;
  } catch (err) {
    reportLoadError('movements', err);
    return assignmentCache;
  }
}

/**
 * Migration stub — logistics is now API-first so there is no local-only data
 * to push. Kept for backward compatibility with usePlanningSync.
 */
export async function syncLogisticsToApi(): Promise<void> {
  // No-op: vehicles, drivers, and assignments are written to the API on every
  // mutation. No localStorage-only data remains to migrate.
}
